import React, { useState, useEffect } from 'react';
import { 
  CalendarIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PlusIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { useFarm } from '../contexts/FarmContext';
import { supabase } from '../config/supabase';
import FarmSearchDropdown from '../components/FarmSearchDropdown';
import OrganizationSelector from '../components/OrganizationSelector';
import FarmSelector from '../components/FarmSelector';

const Attendance = () => {
  const { farms, isAdmin, hasOrganizations, hasFarms } = useFarm();
  const [attendance, setAttendance] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFarmFilter, setSelectedFarmFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [stats, setStats] = useState({
    attendanceRate: 0,
    present: 0,
    absent: 0
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAttendance, setNewAttendance] = useState({
    attendance_date: new Date().toISOString().split('T')[0],
    farm_id: '',
    user_id: '',
    status: 'present',
    check_in_time: '',
    check_out_time: '',
    remarks: ''
  });

  // Load workers (users associated with farms)
  useEffect(() => {
    const loadWorkers = async () => {
      const { data } = await supabase
        .from('farm_user_role_view')
        .select('user_id, name_first, name_last, farm_id, farm_name')
        .order('name_first');
      if (data) {
        const uniqueWorkers = data.reduce((acc, curr) => {
          if (!acc.find(w => w.user_id === curr.user_id)) {
            acc.push(curr);
          }
          return acc;
        }, []);
        setWorkers(uniqueWorkers);
      }
    };
    loadWorkers();
  }, []);

  // Load attendance data
  useEffect(() => {
    const loadAttendance = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from('attendance_view')
          .select('*')
          .order('attendance_date', { ascending: false });

        if (dateRange.start) {
          query = query.gte('attendance_date', dateRange.start);
        }
        if (dateRange.end) {
          query = query.lte('attendance_date', dateRange.end);
        }

        if (selectedFarmFilter !== 'all') {
          query = query.eq('farm_id', parseInt(selectedFarmFilter));
        }

        const { data, error } = await query.limit(500);

        if (error) {
          console.error('Error loading attendance:', error);
          setAttendance([]);
        } else {
          setAttendance(data || []);
          
          // Calculate stats
          const present = data?.filter(a => a.status === 'present' || a.status === 'late').length || 0;
          const absent = data?.filter(a => a.status === 'absent').length || 0;
          const total = present + absent;
          const rate = total > 0 ? Math.round((present / total) * 100) : 0;
          
          setStats({
            attendanceRate: rate,
            present,
            absent
          });
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAttendance();
  }, [selectedFarmFilter, dateRange]);

  // Add new attendance record
  const handleAddAttendance = async () => {
    if (!newAttendance.farm_id || !newAttendance.user_id) {
      alert('Please select a farm and worker');
      return;
    }

    try {
      const { error } = await supabase
        .from('attendance')
        .insert([{
          attendance_date: newAttendance.attendance_date,
          farm_id: parseInt(newAttendance.farm_id),
          user_id: parseInt(newAttendance.user_id),
          status: newAttendance.status,
          check_in_time: newAttendance.check_in_time || null,
          check_out_time: newAttendance.check_out_time || null,
          remarks: newAttendance.remarks || null
        }]);

      if (error) {
        console.error('Error adding attendance:', error);
        alert('Failed to add attendance record');
      } else {
        setShowAddModal(false);
        setNewAttendance({
          attendance_date: new Date().toISOString().split('T')[0],
          farm_id: '',
          user_id: '',
          status: 'present',
          check_in_time: '',
          check_out_time: '',
          remarks: ''
        });
        // Reload attendance
        const { data } = await supabase
          .from('attendance_view')
          .select('*')
          .gte('attendance_date', dateRange.start)
          .lte('attendance_date', dateRange.end)
          .order('attendance_date', { ascending: false })
          .limit(500);
        setAttendance(data || []);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    return timeStr.substring(0, 5);
  };

  const getStatusBadge = (status) => {
    const styles = {
      present: 'bg-green-100 text-green-800',
      absent: 'bg-red-100 text-red-800',
      late: 'bg-yellow-100 text-yellow-800',
      leave: 'bg-blue-100 text-blue-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Attendance</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Track and manage worker attendance across farms</p>
        </div>
        <div className="flex items-center flex-wrap gap-2 sm:gap-3 lg:gap-4">
          {hasOrganizations && <OrganizationSelector />}
          {hasFarms && <FarmSelector />}
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Add Attendance
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
          <h3 className="font-semibold text-gray-900">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Farm</label>
            <FarmSearchDropdown
              farms={farms}
              selectedFarmId={selectedFarmFilter}
              onSelect={setSelectedFarmFilter}
              placeholder="All Farms"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Attendance Rate</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.attendanceRate}%</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-full">
              <UserGroupIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Present</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats.present}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-full">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Absent</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{stats.absent}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-full">
              <XCircleIcon className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Attendance Table */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading attendance records...</p>
        </div>
      ) : attendance.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farm</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check In</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check Out</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {attendance.map((record, idx) => (
                  <tr key={record.id || idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(record.attendance_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{record.full_name || '-'}</div>
                      <div className="text-sm text-gray-500">{record.email || ''}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {record.farm_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatTime(record.check_in_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatTime(record.check_out_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(record.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {record.remarks || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">No attendance records found</p>
          <p className="text-gray-500 text-sm mt-1">Try adjusting your filters or add a new attendance record</p>
        </div>
      )}

      {/* Add Attendance Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add Attendance Record</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={newAttendance.attendance_date}
                  onChange={(e) => setNewAttendance({...newAttendance, attendance_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Farm</label>
                <select
                  value={newAttendance.farm_id}
                  onChange={(e) => setNewAttendance({...newAttendance, farm_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select Farm</option>
                  {farms.filter(f => f.farm_name).map(farm => (
                    <option key={farm.farm_id} value={farm.farm_id}>{farm.farm_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Worker</label>
                <select
                  value={newAttendance.user_id}
                  onChange={(e) => setNewAttendance({...newAttendance, user_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select Worker</option>
                  {workers.map(worker => (
                    <option key={worker.user_id} value={worker.user_id}>
                      {worker.name_first} {worker.name_last}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={newAttendance.status}
                  onChange={(e) => setNewAttendance({...newAttendance, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                  <option value="leave">Leave</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check In</label>
                  <input
                    type="time"
                    value={newAttendance.check_in_time}
                    onChange={(e) => setNewAttendance({...newAttendance, check_in_time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label>
                  <input
                    type="time"
                    value={newAttendance.check_out_time}
                    onChange={(e) => setNewAttendance({...newAttendance, check_out_time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={newAttendance.remarks}
                  onChange={(e) => setNewAttendance({...newAttendance, remarks: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Optional notes..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAttendance}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Add Record
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;
